export const MAX_EQUIVALENT_FOODS = 24;
export const MAX_TOTAL_FOOD_SNAPSHOTS_PER_RECIPE = 48;
export const MAX_TOTAL_FOOD_SNAPSHOTS_PER_MEAL_OPTION = 30;
export const MAX_MEALS_PER_PLAN = 12;
export const MAX_MEAL_OPTIONS_PER_MEAL = 10;
export const MAX_NUTRITION_NAME_LENGTH = 80;
export const MAX_NUTRITION_LONG_TEXT_LENGTH = 1000;
export const MAX_MEAL_NOTES_LENGTH = 500;
export const MAX_NUTRITION_AMOUNT_INPUT_LENGTH = 7;
export const MAX_NUTRITION_MEASURE_GRAMS_INPUT_LENGTH = 10;

export const NUTRITION_LIMITS = Object.freeze({
  MAX_EQUIVALENT_FOODS,
  MAX_TOTAL_FOOD_SNAPSHOTS_PER_RECIPE,
  MAX_TOTAL_FOOD_SNAPSHOTS_PER_MEAL_OPTION,
  MAX_MEALS_PER_PLAN,
  MAX_MEAL_OPTIONS_PER_MEAL,
  MAX_NUTRITION_NAME_LENGTH,
  MAX_NUTRITION_LONG_TEXT_LENGTH,
  MAX_MEAL_NOTES_LENGTH,
  MAX_NUTRITION_AMOUNT_INPUT_LENGTH,
  MAX_NUTRITION_MEASURE_GRAMS_INPUT_LENGTH,
});

function buildValidationResult(ok, code = null, meta = {}) {
  return {
    ok,
    code,
    meta,
  };
}

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function validateMaxLength(value, limit, code, meta = {}) {
  const length = String(value ?? "").length;
  if (length <= limit) {
    return buildValidationResult(true);
  }

  return buildValidationResult(false, code, {
    ...meta,
    limit,
    length,
  });
}

function countPersistedFoodSnapshotsInItem(item = {}) {
  if (!item || typeof item !== "object") {
    return 0;
  }

  if (item.type === "food") {
    return 1;
  }

  if (item.type === "equivalence") {
    return Array.isArray(item.options) ? item.options.length : 0;
  }

  if (item.type === "recipe") {
    return countPersistedFoodSnapshotsInItems(item.items);
  }

  if (Array.isArray(item.options)) {
    return item.options.length;
  }

  if (Array.isArray(item.items)) {
    return countPersistedFoodSnapshotsInItems(item.items);
  }

  return 0;
}

export function countPersistedFoodSnapshotsInItems(items = []) {
  return (Array.isArray(items) ? items : []).reduce(
    (total, item) => total + countPersistedFoodSnapshotsInItem(item),
    0,
  );
}

export function countRuntimeFoodSnapshots(ingredients = []) {
  return (Array.isArray(ingredients) ? ingredients : []).reduce((total, ingredient) => {
    if (!ingredient || typeof ingredient !== "object") {
      return total;
    }

    return total + 1 + countRuntimeFoodSnapshots(ingredient.equivalences);
  }, 0);
}

export function validateEquivalentDraftLimits(equivalentLike = {}) {
  const nameValidation = validateMaxLength(
    equivalentLike?.name,
    MAX_NUTRITION_NAME_LENGTH,
    "nutrition_name_too_long",
    {
      field: "name",
    },
  );
  if (!nameValidation.ok) {
    return nameValidation;
  }

  const options = Array.isArray(equivalentLike?.options) ? equivalentLike.options : [];
  if (options.length > MAX_EQUIVALENT_FOODS) {
    return buildValidationResult(false, "equivalent_foods_limit", {
      limit: MAX_EQUIVALENT_FOODS,
      count: options.length,
    });
  }

  return buildValidationResult(true);
}

export function validateRecipeDraftLimits(recipeLike = {}) {
  const nameValidation = validateMaxLength(
    recipeLike?.name,
    MAX_NUTRITION_NAME_LENGTH,
    "nutrition_name_too_long",
    {
      field: "name",
    },
  );
  if (!nameValidation.ok) {
    return nameValidation;
  }

  const descriptionValidation = validateMaxLength(
    recipeLike?.description,
    MAX_NUTRITION_LONG_TEXT_LENGTH,
    "recipe_description_too_long",
    {
      field: "description",
    },
  );
  if (!descriptionValidation.ok) {
    return descriptionValidation;
  }

  const itemsCount = Array.isArray(recipeLike?.items)
    ? countPersistedFoodSnapshotsInItems(recipeLike.items)
    : countRuntimeFoodSnapshots(recipeLike?.ingredients);

  if (itemsCount > MAX_TOTAL_FOOD_SNAPSHOTS_PER_RECIPE) {
    return buildValidationResult(false, "recipe_foods_limit", {
      limit: MAX_TOTAL_FOOD_SNAPSHOTS_PER_RECIPE,
      count: itemsCount,
    });
  }

  return buildValidationResult(true);
}

function validateMealRecordLimits(meal = {}) {
  const mealNameValidation = validateMaxLength(
    meal?.name,
    MAX_NUTRITION_NAME_LENGTH,
    "meal_name_too_long",
    {
      field: "meal_name",
    },
  );
  if (!mealNameValidation.ok) {
    return mealNameValidation;
  }

  const mealNotesValidation = validateMaxLength(
    meal?.notes,
    MAX_MEAL_NOTES_LENGTH,
    "meal_notes_too_long",
    {
      field: "meal_notes",
    },
  );
  if (!mealNotesValidation.ok) {
    return mealNotesValidation;
  }

  const options = Array.isArray(meal?.options) ? meal.options : [];
  if (options.length > MAX_MEAL_OPTIONS_PER_MEAL) {
    return buildValidationResult(false, "meal_options_limit", {
      limit: MAX_MEAL_OPTIONS_PER_MEAL,
      count: options.length,
      mealId: meal?.id ?? null,
    });
  }

  for (const option of options) {
    const optionNameValidation = validateMaxLength(
      option?.name,
      MAX_NUTRITION_NAME_LENGTH,
      "meal_option_name_too_long",
      {
        field: "meal_option_name",
      },
    );
    if (!optionNameValidation.ok) {
      return optionNameValidation;
    }

    const foodSnapshotCount = countPersistedFoodSnapshotsInItems(option?.items);
    if (foodSnapshotCount > MAX_TOTAL_FOOD_SNAPSHOTS_PER_MEAL_OPTION) {
      return buildValidationResult(false, "meal_option_foods_limit", {
        limit: MAX_TOTAL_FOOD_SNAPSHOTS_PER_MEAL_OPTION,
        count: foodSnapshotCount,
        optionId: option?.id ?? null,
        optionName: option?.name ?? null,
        mealId: meal?.id ?? null,
      });
    }
  }

  return buildValidationResult(true);
}

export function validatePlanDraftLimits(planLike = {}) {
  const nameValidation = validateMaxLength(
    planLike?.name,
    MAX_NUTRITION_NAME_LENGTH,
    "nutrition_name_too_long",
    {
      field: "name",
    },
  );
  if (!nameValidation.ok) {
    return nameValidation;
  }

  const planNotesValidation = validateMaxLength(
    planLike?.notes,
    MAX_NUTRITION_LONG_TEXT_LENGTH,
    "plan_notes_too_long",
    {
      field: "notes",
    },
  );
  if (!planNotesValidation.ok) {
    return planNotesValidation;
  }

  const meals = Array.isArray(planLike?.meals) ? planLike.meals : [];
  if (meals.length > MAX_MEALS_PER_PLAN) {
    return buildValidationResult(false, "plan_meals_limit", {
      limit: MAX_MEALS_PER_PLAN,
      count: meals.length,
    });
  }

  for (const meal of meals) {
    const mealValidation = validateMealRecordLimits(meal);
    if (!mealValidation.ok) {
      return mealValidation;
    }
  }

  return buildValidationResult(true);
}

export function createNutritionLimitError(validationResult) {
  const error = new Error(
    `Nutrition limit exceeded: ${validationResult?.code ?? "unknown_limit"}`,
  );
  error.code = validationResult?.code ?? "unknown_limit";
  error.nutritionLimit = validationResult ?? buildValidationResult(false);
  return error;
}

export function assertNutritionLimit(validationResult) {
  if (validationResult?.ok === false) {
    throw createNutritionLimitError(validationResult);
  }

  return validationResult;
}

export function getNutritionLimitMessageDescriptor(validationResult) {
  if (!validationResult?.code) {
    return null;
  }

  switch (validationResult.code) {
    case "equivalent_foods_limit":
      return {
        title: "nutritionLimitReachedTitle-string",
        description: "nutritionEquivalentFoodsLimitDescription-string",
      };
    case "recipe_foods_limit":
      return {
        title: "nutritionLimitReachedTitle-string",
        description: "nutritionRecipeFoodsLimitDescription-string",
      };
    case "meal_option_foods_limit":
      return {
        title: "nutritionLimitReachedTitle-string",
        description: "nutritionMealOptionFoodsLimitDescription-string",
      };
    case "plan_meals_limit":
      return {
        title: "nutritionLimitReachedTitle-string",
        description: "nutritionPlanMealsLimitDescription-string",
      };
    case "meal_options_limit":
      return {
        title: "nutritionLimitReachedTitle-string",
        description: "nutritionMealOptionsLimitDescription-string",
      };
    case "nutrition_name_too_long":
    case "meal_name_too_long":
    case "meal_option_name_too_long":
      return {
        title: "nutritionLimitReachedTitle-string",
        description: "nutritionNameTooLongLimitDescription-string",
      };
    case "recipe_description_too_long":
      return {
        title: "nutritionLimitReachedTitle-string",
        description: "nutritionRecipeDescriptionTooLongLimitDescription-string",
      };
    case "plan_notes_too_long":
      return {
        title: "nutritionLimitReachedTitle-string",
        description: "nutritionPlanNotesTooLongLimitDescription-string",
      };
    case "meal_notes_too_long":
      return {
        title: "nutritionLimitReachedTitle-string",
        description: "nutritionMealNotesTooLongLimitDescription-string",
      };
    default:
      return {
        title: "nutritionLimitReachedTitle-string",
        description: "nutritionLimitReachedDescription-string",
      };
  }
}

export function getNutritionLimitMessageVariables(validationResult) {
  return {
    limit: validationResult?.meta?.limit ?? null,
    count: validationResult?.meta?.count ?? null,
  };
}

export function shouldShowNutritionLimitWarning(validationResult) {
  return [
    "equivalent_foods_limit",
    "recipe_foods_limit",
    "meal_option_foods_limit",
    "plan_meals_limit",
    "meal_options_limit",
  ].includes(validationResult?.code);
}
